<template>
    <div>
        <TagSelector mainHeading="" optionName="users"
            :availableOptionsHeading="availableOptionsHeading"
            :selectedOptionsHeading="selectedOptionsHeading"
            :showDefaultMessage="false"
            :initial-skills="availableActivities" v-model:interests="userActivities"
            :onSave="readOnly ? null : handleSaveActivities"
            :loading="loading" :isSaving="submitting" :disabled="readOnly" />
    </div>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { useStore } from "vuex";
import TagSelector from "@/components/common/TagSelector.vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";

defineProps({
    readOnly: { type: Boolean, default: false }
});

const store = useStore();
const route = useRoute();
const toast = useToast();

// ✅ Get IDs from route params
const instanceWorkgroupId = route.params.instanceworkgroupid;

// ✅ Reactive state
const availableActivities = ref([]); // all users
const userActivities = ref([]); // current admins
const adminsData = ref(null);

const loading = computed(() => store.getters.instanceWorkgroupLoading);
const submitting = ref(false);

// ✅ Dynamic headings based on API response
const availableOptionsHeading = computed(() => {
    return "All Users";
});

const selectedOptionsHeading = computed(() => {
    if (!adminsData.value) return "Current Admins";
    const workgroupName = adminsData.value.workgroup_name || "";
    return `Current Admins: ${workgroupName}`;
});

// ✅ Load instance workgroup admins
const loadInstanceWorkgroupAdmins = async () => {
    try {
        const data = await store.dispatch("getInstanceWorkgroupAdmins", instanceWorkgroupId);
        
        adminsData.value = data;

        // Map all_users to available options
        const allUsers = data?.all_users || [];
        availableActivities.value = allUsers.map((user) => ({
            id: user.id,
            name: user.full_name || `${user.firstname} ${user.lastname}`,
            description: user.email || "",
        }));

        // Map current_admins to selected options
        const currentAdmins = data?.current_admins || [];
        userActivities.value = currentAdmins.map((user) => ({
            id: user.id,
            name: user.full_name || `${user.firstname} ${user.lastname}`,
            description: user.email || "",
        }));
    } catch (error) {
        console.error("Failed to load instance workgroup admins:", error);
        toast.error("Failed to load instance workgroup admins", { position: "top-right" });
    }
};

// ✅ Save handler for instance workgroup admins
const handleSaveActivities = async (selected) => {
    submitting.value = true;
    try {
        const userIds = selected.map((x) => x.id);

        await store.dispatch("saveInstanceWorkgroupAdmins", {
            workgroupId: instanceWorkgroupId,
            userIdsArray: userIds,
        });

        toast.success(`Successfully saved ${selected.length} admin(s)`, {
            position: "top-right"
        });

        // Reload data after successful save
        await loadInstanceWorkgroupAdmins();
    } catch (error) {
        console.error("Failed to save instance workgroup admins:", error);
        toast.error("Failed to save instance workgroup admins", { position: "top-right" });
    } finally {
        submitting.value = false;
    }
};

onMounted(async () => {
    await loadInstanceWorkgroupAdmins();
});
</script>